import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "../../../components/Logo";
import ProgressSteps from "../../../components/ProgressSteps";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { getSignupInfo, getSpecialites, type SpecialiteOption } from "../../../api/authProfessional";
import { RequiredMark } from "../../../components/RequiredMark";

const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/;

export default function ProSignupInfo1() {
  const navigate = useNavigate();
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [genre, setGenre] = useState("");
  const [profil, setProfil] = useState("");
  const [specialiteAutre, setSpecialiteAutre] = useState("");
  const [signupBlocked, setSignupBlocked] = useState<string | null>(null);
  const [specialites, setSpecialites] = useState<SpecialiteOption[]>([]);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      const res = await getSignupInfo();
      if (!mounted) return;
      if (!res.ok) return;
      const allowed = res.data.signupAllowed !== false;
      if (!allowed) {
        setSignupBlocked("L'inscription professionnelle n'est pas ouverte pour le moment.");
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      // Liste des spécialités récupérée depuis le backend (source unique de vérité,
      // alignée sur l'enum réel accepté à l'inscription) — jamais figée côté front.
      const res = await getSpecialites();
      if (!mounted) return;
      if (res.ok) setSpecialites(res.data);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("pro.signup");
      if (!raw) return;
      const draft = JSON.parse(raw) as Record<string, unknown>;
      if (typeof draft.nom === "string") setNom(draft.nom);
      if (typeof draft.prenom === "string") setPrenom(draft.prenom);
      if (typeof draft.genre === "string") setGenre(draft.genre);
      if (typeof draft.profil === "string") setProfil(draft.profil);
      if (typeof draft.specialite_autre === "string") setSpecialiteAutre(draft.specialite_autre);
    } catch {
      /* ignore */
    }
  }, []);

  const nomValid = useMemo(
    () => nom.trim().length >= 2 && nameRegex.test(nom.trim()),
    [nom]
  );
  const prenomValid = useMemo(
    () => prenom.trim().length >= 2 && nameRegex.test(prenom.trim()),
    [prenom]
  );
  const isAutre = profil === "autre";
  const canProceed =
    nomValid &&
    prenomValid &&
    genre !== "" &&
    profil !== "" &&
    (!isAutre || specialiteAutre.trim().length >= 2) &&
    !signupBlocked;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canProceed) return;
    try {
      const raw = localStorage.getItem("pro.signup");
      const base = raw ? JSON.parse(raw) : {};
      localStorage.setItem(
        "pro.signup",
        JSON.stringify({
          ...base,
          nom,
          prenom,
          genre,
          profil,
          specialite: profil,
          // Envoyé en majuscules — déjà forcé à la saisie, revalidé/renormalisé côté backend.
          specialite_autre: isAutre ? specialiteAutre.trim().toUpperCase() : undefined,
        })
      );
    } catch {
      /* ignore */
    }
    navigate('/professionnel/inscription/identifiants');
  };

  return (
    <div className="relative h-screen overflow-hidden">
      <img src={import.meta.env.BASE_URL + 'maps-reset-side.jpg'} alt="pro-signup-bg" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/30" />

      <div className="absolute inset-0 z-10 px-4 md:px-8 py-6 md:py-8 flex">
        <div className="max-w-6xl mx-auto flex flex-col w-full">
          <div className="pl-2 md:pl-6 pt-2 md:pt-4">
            <Link to="/professionnel/connexion" className="inline-flex items-center gap-2 text-white/90">
              <ArrowLeft size={18} /> Retour
            </Link>
          </div>

          <div className="mt-auto flex items-end justify-between gap-6 pl-2 md:pl-6 pr-4 md:pr-8 pb-4 md:pb-8">
            <div className="w-full max-w-2xl min-h-[520px] bg-white/95 backdrop-blur rounded-[12px] shadow p-8 md:p-10">
              <h1 className="text-2xl font-semibold" style={{ color: '#0B1F16' }}>Créez votre compte</h1>
              {signupBlocked && (
                <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">
                  {signupBlocked}
                </div>
              )}

              <form className="mt-6 space-y-4" onSubmit={onSubmit}>
                <div className="space-y-2">
                  <label className="text-sm" style={{ color: '#0B1F16' }}>
                    Nom
                    <RequiredMark />
                  </label>
                  <input
                    placeholder="Nom"
                    value={nom}
                    onChange={(event) => setNom(event.target.value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ' -]/g, ''))}
                    className={`w-full h-14 rounded-[10px] border-2 px-3 text-base ${nomValid || nom === '' ? '' : 'border-red-400'}`}
                    style={{ borderColor: nomValid || nom === '' ? '#D1E2DD' : '#F87171' }}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm" style={{ color: '#0B1F16' }}>
                    Prénom
                    <RequiredMark />
                  </label>
                  <input
                    placeholder="Prénom"
                    value={prenom}
                    onChange={(event) => setPrenom(event.target.value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ' -]/g, ''))}
                    className={`w-full h-14 rounded-[10px] border-2 px-3 text-base ${prenomValid || prenom === '' ? '' : 'border-red-400'}`}
                    style={{ borderColor: prenomValid || prenom === '' ? '#D1E2DD' : '#F87171' }}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm" style={{ color: '#0B1F16' }}>
                    Votre sexe
                    <RequiredMark />
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button type="button" onClick={() => setGenre('Homme')} className={`h-12 rounded-[10px] border-2 font-semibold ${genre === 'Homme' ? 'bg-[#00553E] text-white border-[#00553E]' : ''}`} style={{ borderColor: '#D1E2DD' }}>
                      Homme
                    </button>
                    <button type="button" onClick={() => setGenre('Femme')} className={`h-12 rounded-[10px] border-2 font-semibold ${genre === 'Femme' ? 'bg-[#00553E] text-white border-[#00553E]' : ''}`} style={{ borderColor: '#D1E2DD' }}>
                      Femme
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm" style={{ color: '#0B1F16' }}>
                    Profil
                    <RequiredMark />
                  </label>
                  <select value={profil} onChange={(event) => setProfil(event.target.value)} className="w-full h-14 rounded-[10px] border-2 px-3 bg-white text-base" style={{ borderColor: '#D1E2DD' }}>
                    <option value="" disabled>Choisir…</option>
                    {specialites.map((option) => {
                      const value = option.value ?? option.code ?? option.libelle ?? option.label ?? "";
                      const label = option.libelle ?? option.label ?? value;
                      return (
                        <option key={value} value={value}>{label}</option>
                      );
                    })}
                  </select>
                </div>

                {isAutre && (
                  <div className="space-y-2">
                    <label className="text-sm" style={{ color: '#0B1F16' }}>
                      Précisez votre spécialité
                      <RequiredMark />
                    </label>
                    <input
                      placeholder="EX. OSTÉOPATHE"
                      value={specialiteAutre}
                      onChange={(event) => setSpecialiteAutre(event.target.value.toUpperCase())}
                      className="w-full h-14 rounded-[10px] border-2 px-3 text-base uppercase"
                      style={{ borderColor: '#D1E2DD' }}
                    />
                  </div>
                )}

                <button type="submit" className="w-full h-14 rounded-[10px] font-medium cursor-pointer" style={{ backgroundColor: canProceed ? '#00553E' : '#DCEFE9', color: canProceed ? '#FFFFFF' : '#7aa79a' }} disabled={!canProceed}>
                  Suivant
                </button>
              </form>
            </div>

            <div className="hidden md:block shrink-0">
              <img src={import.meta.env.BASE_URL + 'Group 5.png'} alt="agabaza-logo" className="w-auto" style={{ height: 144 }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

